"use client"

import React, { useState, useEffect, useMemo } from "react"
import { getToken } from "../auth"
import {
  BarChart3,
  Users,
  TrendingUp,
  Activity,
  Award,
  User,
  Phone,
  Target,
  Download,
} from "lucide-react"

class AnalyticsErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }
  static getDerivedStateFromError(error) {
    return { hasError: true }
  }
  componentDidCatch(error, errorInfo) {
    console.error("Analytics error:", error, errorInfo)
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-[#CED7DB] flex items-center justify-center">
          <div className="bg-white p-8 rounded-xl shadow-lg text-center">
            <h2 className="text-xl font-bold text-red-600 mb-4">Analytics Error</h2>
            <p className="text-gray-600 mb-4">Something went wrong loading the analytics.</p>
            <button onClick={() => window.location.reload()} className="bg-[#0A4B4B] text-white px-6 py-2 rounded-lg">
              Reload Page
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

const RefreshButton = ({ onRefresh, loading }) => (
  <button
    onClick={onRefresh}
    disabled={loading}
    className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
    title="Refresh data"
  >
    <svg
      className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
      />
    </svg>
    {loading ? "Refreshing..." : "Refresh"}
  </button>
)

const AnalyticsPage = () => {
  const [overview, setOverview] = useState(null)
  const [users, setUsers] = useState([])
  const [agents, setAgents] = useState([])
  const [recentActivity, setRecentActivity] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState("overview")
  const [timeRange, setTimeRange] = useState("week")
  const [selectedAgent, setSelectedAgent] = useState("all")
  const [selectedCallType, setSelectedCallType] = useState("all")

  useEffect(() => {
    fetchAnalytics()
  }, [])

  useEffect(() => {
    const autoRefresh = localStorage.getItem('analytics_auto_refresh') === 'true'
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchAnalytics()
      }, 60000)
      return () => clearInterval(interval)
    }
  }, [])

  const fetchAnalytics = async () => {
    setLoading(true)
    try {
      const token = getToken()
      const headers = { Authorization: `Bearer ${token}` }
      const [overviewRes, usersRes, agentsRes, activityRes] = await Promise.all([
        fetch("https://call-analyzer-backend.onrender.com/analytics/overview", { headers }),
        fetch("https://call-analyzer-backend.onrender.com/analytics/users", { headers }),
        fetch("https://call-analyzer-backend.onrender.com/analytics/agents", { headers }),
        fetch("https://call-analyzer-backend.onrender.com/analytics/recent-activity", { headers }),
      ])
      if (overviewRes.ok) setOverview(await overviewRes.json())
      if (usersRes.ok) setUsers((await usersRes.json())?.users || [])
      if (agentsRes.ok) setAgents((await agentsRes.json())?.agents || [])
      if (activityRes.ok) setRecentActivity((await activityRes.json())?.recent_activities || [])
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const handleExport = () => {
    try {
      const payload = { overview, totals, byCallType, agents, users, recentActivity, exportedAt: new Date().toISOString() }
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `analytics_export_${new Date().toISOString().replace(/[:.]/g, "-")}.json`
      a.click()
      URL.revokeObjectURL(url)
      const successDiv = document.createElement("div")
      successDiv.className = "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50"
      successDiv.textContent = "Analytics exported successfully!"
      document.body.appendChild(successDiv)
      setTimeout(() => document.body.removeChild(successDiv), 3000)
    } catch (error) {
      console.error("Export failed:", error)
      alert("Failed to export data. Please try again.")
    }
  }

  const handleCSVExport = () => {
    const headers = ["Agent Name", "Total Calls", "Average Score", "Grade", "Min Score", "Max Score", "Unique Users"]
    const rows = agents.map(a => [
      a.agent_name || "Unknown",
      a.total_calls || 0,
      a.average_score || 0,
      a.performance_grade || "N/A",
      a.min_score || 0,
      a.max_score || 0,
      a.unique_users || 0
    ])
    const csvContent = [headers.join(","), ...rows.map(row => row.join(","))].join("\n")
    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `agent_performance_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const getScoreColor = (score) => {
    if (Number(score) >= 8) return "text-green-600 bg-green-100"
    if (Number(score) >= 6) return "text-yellow-600 bg-yellow-100"
    return "text-red-600 bg-red-100"
  }

  const totals = useMemo(() => {
    const totalCalls = agents.reduce((sum, a) => sum + (Number(a.total_calls) || 0), 0)
    const totalAppointments = agents.reduce((sum, a) => sum + (Number(a.total_appointments) || 0), 0)
    const avgScore = overview?.average_score || 0
    const conversionRate = totalCalls > 0 && totalAppointments > 0 ? Number(((totalAppointments / totalCalls) * 100).toFixed(1)) : 0
    const thisWeek = overview?.recent_analyses || 0
    return { totalCalls, totalAppointments, avgScore, conversionRate, thisWeek }
  }, [agents, overview])

  const byCallType = useMemo(() => {
    const map = {}
    for (const item of recentActivity) {
      const ct = item?.call_type || "unknown"
      if (!map[ct]) map[ct] = { calls: 0, totalScore: 0, countScore: 0 }
      map[ct].calls += 1
      if (item?.score != null) {
        map[ct].totalScore += Number(item.score)
        map[ct].countScore += 1
      }
    }
    return Object.entries(map).map(([type, data]) => ({
      type,
      calls: data.calls,
      avgScore: data.countScore ? Number((data.totalScore / data.countScore).toFixed(1)) : 0,
      appointments: 0,
      rate: 0
    }))
  }, [recentActivity])

  const CallTypeCard = ({ row, totalCalls }) => {
    const percentage = totalCalls > 0 ? ((row.calls / totalCalls) * 100).toFixed(1) : 0
    return (
      <div className="border rounded-lg p-4 bg-white relative">
        <div className="absolute top-2 right-2 text-xs text-gray-500">{percentage}% of total</div>
        <div className="flex items-center justify-between mb-2">
          <span className="font-semibold capitalize text-gray-700">{row.type}</span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(row.avgScore)}`}>{row.avgScore}/10</span>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-[#CED7DB] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A4B4B] mb-4"></div>
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    )
  }

  return (
    <AnalyticsErrorBoundary>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-[#CED7DB] p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-[#CED7DB]/30 mb-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-3xl font-bold text-[#0A4B4B] mb-1">Analytics Dashboard</h1>
                <p className="text-gray-600">Live insights from your real data (no mock values)</p>
              </div>
              <div className="flex gap-3 items-center">
                <select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0A4B4B]"
                  title="Client-side time filter (cosmetic until API supports)"
                >
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="quarter">This Quarter</option>
                </select>
                <RefreshButton onRefresh={() => fetchAnalytics()} loading={loading} />
                <button onClick={handleExport} className="bg-[#0A4B4B] hover:bg-[#093f3f] text-white px-4 py-2 rounded-lg flex items-center gap-2">
                  <Download className="h-4 w-4" /> Export JSON
                </button>
                <button onClick={handleCSVExport} className="bg-white text-[#0A4B4B] border border-[#0A4B4B] hover:bg-[#0A4B4B] hover:text-white px-4 py-2 rounded-lg flex items-center gap-2">
                  <Download className="h-4 w-4" /> Export CSV
                </button>
                <BarChart3 className="h-10 w-10 text-[#0A4B4B]" />
              </div>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-[#CED7DB]/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Users</p>
                  <p className="text-2xl font-bold text-[#0A4B4B]">{overview?.total_users ?? 0}</p>
                </div>
                <Users className="h-8 w-8 text-blue-500" />
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-[#CED7DB]/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active Users</p>
                  <p className="text-2xl font-bold text-green-600">{overview?.active_users ?? 0}</p>
                </div>
                <Activity className="h-8 w-8 text-green-500" />
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-[#CED7DB]/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Calls</p>
                  <p className="text-2xl font-bold text-purple-600">{totals.totalCalls}</p>
                </div>
                <Phone className="h-8 w-8 text-purple-500" />
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-[#CED7DB]/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Average Score</p>
                  <p className="text-2xl font-bold text-orange-600">{totals.avgScore}/10</p>
                </div>
                <Award className="h-8 w-8 text-orange-500" />
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-[#CED7DB]/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">This Week</p>
                  <p className="text-2xl font-bold text-indigo-600">{totals.thisWeek}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-indigo-500" />
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-[#CED7DB]/30 mb-6">
            <div className="p-4 flex flex-wrap gap-3 items-center">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700">Agent</label>
                <select
                  value={selectedAgent}
                  onChange={(e) => setSelectedAgent(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0A4B4B]"
                >
                  <option value="all">All</option>
                  {agents.map((a, i) => (
                    <option key={i} value={a.agent_name || `agent-${i}`}>
                      {a.agent_name || `Agent ${i + 1}`}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700">Call Type</label>
                <select
                  value={selectedCallType}
                  onChange={(e) => setSelectedCallType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0A4B4B]"
                >
                  <option value="all">All</option>
                  {[...new Set(recentActivity.map((r) => r.call_type || "unknown"))].map((ct) => (
                    <option key={ct} value={ct}>{ct}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Call Type Performance */}
          <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-[#CED7DB]/30 mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Performance by Call Type</h2>
            {byCallType.length === 0 ? (
              <div className="text-sm text-gray-600">No call activity yet.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {byCallType
                  .filter((row) => (selectedCallType === "all" ? true : row.type === selectedCallType))
                  .map((row) => (
                    <CallTypeCard key={row.type} row={row} totalCalls={totals.totalCalls} />
                  ))}
              </div>
            )}
          </div>

          {/* Agents */}
          <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-[#CED7DB]/30 mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Agent Performance</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 text-left">
                    <th className="py-2 px-3">Agent</th>
                    <th className="py-2 px-3">Calls</th>
                    <th className="py-2 px-3">Appointments</th>
                    <th className="py-2 px-3">Avg Score</th>
                    <th className="py-2 px-3">Min</th>
                    <th className="py-2 px-3">Max</th>
                    <th className="py-2 px-3">Users</th>
                  </tr>
                </thead>
                <tbody>
                  {agents
                    .filter((a) => (selectedAgent === "all" ? true : a.agent_name === selectedAgent))
                    .map((a, idx) => (
                      <tr key={`${a.agent_name || "unknown"}-${idx}`} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-2 px-3 font-medium text-gray-900">{a.agent_name || "Unknown"}</td>
                        <td className="py-2 px-3">{a.total_calls || 0}</td>
                        <td className="py-2 px-3">{a.total_appointments || 0}</td>
                        <td className="py-2 px-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(a.average_score)}`}>
                            {a.average_score || 0}/10
                          </span>
                        </td>
                        <td className="py-2 px-3">{a.min_score ?? "-"}</td>
                        <td className="py-2 px-3">{a.max_score ?? "-"}</td>
                        <td className="py-2 px-3">{a.unique_users ?? "-"}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-[#CED7DB]/30 mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Recent Call Analyses</h2>
            <div className="space-y-3">
              {recentActivity
                .filter((a) => (selectedCallType === "all" ? true : (a.call_type || "unknown") === selectedCallType))
                .map((activity, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg border">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <User className="h-5 w-5 text-gray-400 mr-3" />
                        <div>
                          <div className="font-medium text-gray-900">{activity.agent_name}</div>
                          <div className="text-sm text-gray-600">by {activity.user_email} • {(activity.call_type || "unknown")} call</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(activity.score)}`}>
                          {activity.score}/10
                        </span>
                        <span className="text-sm text-gray-500">{activity.created_at ? new Date(activity.created_at).toLocaleDateString() : ""}</span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </AnalyticsErrorBoundary>
  )
}

export default AnalyticsPage
